import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import TableFilterCardListView from '@/cms/components/CardList/tableFilterCardList/index';
import { TableFilterCardItemFieldProp } from '@/cms/components/CardList/tableFilterCardList/item';
import CreateQuoteModal from '@/cms/components/dialogs/createQuote';
import TableColumnFilterView from '@/cms/components/tableColumnFilter/common';
import { usePageJump } from '@/cms/hooks/router/usePageJump';
import {
  QuoteListTableColumnFilterEnum,
  quoteListTableColumnFilterList,
  quoteListTableColumnFilterSaveKey,
  useQuoteListTableColumnFilter,
} from '@/cms/hooks/tableColumnFilter/useQuoteListTableColumnFilter';
import { Routes } from '@/cms/util/routes';
import { saveQuoteListCardData } from '@/cms/util/storage';
import { AdjustmentsIcon, DownloadIcon } from '@heroicons/react/outline';
import { QuoteBusinessSourceEnum } from '@jove-frontend/enums';
import { JoveButton, JoveDateRangePicker } from '@jove-frontend/mui';
import {
  Button,
  EntityName,
  FormatType,
  QuoteResponse,
  SortOrderEnum,
  isPublishOnline,
  prepareQuoteFilterParams,
  useAuthStore,
  useFileReport,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import { Add, Refresh } from '@mui/icons-material';
import { cloneDeep } from 'lodash';
import Layout from '../../components/Layout';
import { QuoteFilters } from '../../components/Quote/QuoteFilters';
import { QuoteTable } from '../../components/Quote/QuoteTable';
import { SortOrder, TCatalogStore } from '../../store/catalog/CatalogStore';
import { useQuotesCatalog, useQuotesCatalogPageStore } from '../../store/quote/QuoteCatalogPageStore';
import { withAuthRequired } from '../../util/withAuthRequired';

export const QuoteListPage = () => {
  const router = useRouter();
  const { $t } = useLocalTranslation();
  const catalogStore = useQuotesCatalog((store) => ({
    catalogState: store.catalogState,
    catalogActions: store.catalogActions,
    sortState: store.sortState,
    sortActions: store.sortActions,
    filterState: store.filterState,
    filterActions: store.filterActions,
    resetState: store.resetState,
  })) as TCatalogStore<QuoteResponse & { assignee: any; organization: any }>;

  const {
    catalogState: { isLoading },
    catalogActions: { fetch, initialize: catalogInit },
    filterState,
    filterActions: { initialize: filtersInit, setFilter, clearFilters },
    sortState,
    sortActions: { setSort },
    resetState,
  } = catalogStore;

  const [{ catalogColumns, statusCountList }, { fetchQuotes, initializeFilters, updateQuoteCountStatusItem, cleanQuoteStatusCountInitialInfo }] =
    useQuotesCatalogPageStore((store) => [store.state, store.actions]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const { showColumList, setShowColumList } = useQuoteListTableColumnFilter();
  const [realShowColumnList, setRealShowColumnList] = useState(catalogColumns);
  const { isGeneratingReport, generateAndDownloadReport } = useFileReport();
  const { cleanAllSessionInfo } = usePageJump();
  const {
    state: { userRoles },
  } = useAuthStore();

  useEffect(() => {
    cleanAllSessionInfo();
    const startHandler = (pathname: string) => {
      if (!pathname.includes('/quotes')) {
        resetState();
      }
    };

    router.events.on('routeChangeStart', startHandler);

    return () => {
      router.events.off('routeChangeStart', startHandler);
      cleanQuoteStatusCountInitialInfo();
    };
  }, []);

  useEffect(() => {
    setSort('created_at', false, SortOrder.desc);
    catalogInit(fetchQuotes);
    initializeFilters().then((filters) => filtersInit(filters));
  }, [catalogInit, fetchQuotes, filtersInit, initializeFilters, resetState, setSort]);

  useEffect(() => {
    // Follow the column order configured in the column filter popover; copyURL is pinned at the end as the Operate column
    const orderedList = (showColumList || [])
      .filter((item) => !!item?.checked)
      .map((item) => (catalogColumns || []).find((aItem) => aItem.name === item.value))
      .filter(Boolean)
      .map((item) =>
        item.name === QuoteListTableColumnFilterEnum.copyURL ? { ...item, label: $t('common:operation'), labelKey: 'common:operation' } : item
      );
    setRealShowColumnList(orderedList);
  }, [showColumList]);

  const quoteStatusCountChange = (list) => {
    const field = cloneDeep(filterState?.filters?.status);
    const tagField = cloneDeep(filterState?.filters?.tags);
    const renewStatusList = cloneDeep(filterState?.filters?.renewStatusList);
    const countTagList = cloneDeep(filterState?.filters?.countTags);

    const commonDataList = [field, tagField, renewStatusList];
    list.forEach((item) => {
      commonDataList.forEach((dataList) => {
        const temp = dataList?.find((aItem) => aItem.value === item.key);
        if (temp) {
          temp.checked = item.isSelected;
        }
      });
      const countTemp = countTagList?.find((aItem) => aItem.value === item.key);
      if (countTemp) {
        countTemp.checked = item.isSelected;
        countTemp.filterValue = Number(item.filterValue || 0);
      }
    });

    setFilter('status', commonDataList[0]);
    setFilter('tags', commonDataList[1]);
    setFilter('renewStatusList', commonDataList[2]);
    setFilter('countTags', countTagList);
    saveQuoteListCardData({ countTagList: countTagList });
  };

  const createQuoteAction = () => {
    setShowCreateQuote(true);
  };

  const closeCreateQuoteModalAction = () => {
    setShowCreateQuote(false);
  };

  const createTimeChangeAction = (value) => {
    setFilter('createTimeRange', value);
    fetch(true);
  };
  const paymentTimeChangeAction = (value) => {
    setFilter('paymentTimeRange', value);
    fetch(true);
  };
  const purchaseTimeChangeAction = (value) => {
    setFilter('purchaseTimeRange', value);
    fetch(true);
  };
  const showColumnChangeAction = (list) => {
    setShowColumList(list);
  };

  const cardItemChangeAction = (index: number, item: TableFilterCardItemFieldProp) => {
    const list = cloneDeep(statusCountList);
    list.splice(index, 1, item);
    updateQuoteCountStatusItem(list);
    quoteStatusCountChange(list);
    fetch();
  };

  // Download filtered quotes as CSV
  const downloadFilteredQuotes = async () => {
    try {
      const currentFilters = filterState.filters || {};

      const filterParams = prepareQuoteFilterParams({
        ...currentFilters,
        limit: 10000, // Limit the max number of records to download for performance evaluation
        offset: 0,
        sortColumn: sortState?.column || 'created_at',
        sortOrder: sortState?.order || SortOrderEnum.DESC,
      });

      await generateAndDownloadReport({
        entityName: EntityName.QUOTE,
        formatType: FormatType.CSV,
        filterSettings: filterParams,
        filename: `quotes-export-${new Date().toISOString().split('T')[0]}`,
      });
    } catch (error) {
      console.error('Error downloading quotes:', error);
    }
  };

  const refreshAction = () => {
    fetch(true);
  };

  const createQuoteSuccessAction = (item) => {
    router.push(Routes.quoteUniversalDetailPage(item.id));
  };

  return (
    <Layout>
      <header className="mb-5 flex flex-row items-center justify-between">
        <div className="flex flex-row items-center space-x-3">
          <h4 className="h4 text-black">{$t('pages:menus.quotes')}</h4>
          <Refresh className="text-brand-secondary h-7 w-7 cursor-pointer" onClick={refreshAction} />
        </div>
        <div className="flex flex-row">
          <TableColumnFilterView
            className="mr-3"
            emitWhenInitial
            enableDragSort
            fixedEndValues={[QuoteListTableColumnFilterEnum.copyURL]}
            saveKey={quoteListTableColumnFilterSaveKey}
            defaultColumnList={quoteListTableColumnFilterList({ sourceType: QuoteBusinessSourceEnum.quote })}
            columnChange={showColumnChangeAction}
            btnClassName="text-[#0E35D1]"
          />
          {!userRoles?.includes('ga-admin' as any) && (
            <JoveButton variant="contained" startIcon={<Add />} onClick={createQuoteAction}>
              {$t('pages:quotes.new_quote')}
            </JoveButton>
          )}
        </div>
      </header>

      <div className="flex flex-col rounded-2xl bg-white px-2 lg:px-0">
        {statusCountList.length > 0 && (
          <>
            <TableFilterCardListView className="ml-6 mt-6" list={statusCountList} itemChange={cardItemChangeAction} disabled={isLoading} />
            <div className="bg-black-10 mt-4 h-px"></div>
          </>
        )}
        <div className="mt-3 flex flex-row items-center justify-between px-6">
          <div className="flex flex-row space-x-5">
            <JoveDateRangePicker
              value={filterState?.filters?.createTimeRange}
              valueChange={createTimeChangeAction}
              label={$t('pages:quotes.create_time')}
            />
            <JoveDateRangePicker
              value={filterState?.filters?.paymentTimeRange}
              valueChange={paymentTimeChangeAction}
              label={$t('pages:quotes.payment_time')}
            />
            {!isPublishOnline() && (
              <JoveDateRangePicker
                value={filterState?.filters?.purchaseTimeRange}
                valueChange={purchaseTimeChangeAction}
                label={$t('page:quoteListTableColumnFilter.purchased_at')}
              />
            )}
          </div>
          <div className="flex flex-row space-x-2">
            {!userRoles?.includes('ga-admin' as any) && (
              <JoveButton
                color="primary"
                variant="outlined"
                startIcon={<DownloadIcon className="h-5 w-5" />}
                onClick={downloadFilteredQuotes}
                disabled={isLoading || isGeneratingReport}
              >
                {isGeneratingReport ? $t('pages:quotes.report_being_generated') : $t('pages:quotes.download_filtered_view')}
              </JoveButton>
            )}

            <Button color="secondary-outline" size="sm" className="rounded-lg" onClick={() => setShowFilters(true)} disabled={isLoading}>
              <AdjustmentsIcon height={18} className="mr-1 rotate-180 text-black/70" />
              <span className="text-sm">{$t('pages:quotes.filters')}</span>
            </Button>
          </div>
        </div>
        <div className="bg-black-10 mt-4 h-px"></div>
        <QuoteTable className="mt-6 px-6" store={catalogStore} catalogColumns={realShowColumnList} isAdminControlPanel={true} />
      </div>

      <QuoteFilters
        filterState={filterState}
        isOpen={showFilters}
        handleFieldChange={setFilter}
        applyFilters={() => {
          fetch(true);
          setShowFilters(false);
        }}
        clearFilters={() => {
          setShowFilters(false);
          clearFilters();
          fetch(true);
        }}
        onClose={() => {
          setShowFilters(false);
        }}
      />

      {showCreateQuote && <CreateQuoteModal close={closeCreateQuoteModalAction} success={createQuoteSuccessAction} />}
    </Layout>
  );
};

export default withAuthRequired(QuoteListPage);
